import { OneOrManySchema, QueryBooleanSchema, SORT_QUERY_PARAMETER_REGEX, SortOrder, } from 'notable-common';
import { z } from 'zod';
import { AssistantChatSessionModality } from './chatSession.js';
import { AssistantVoiceCallType } from './voiceCall.js';
export const ASSISTANT_LIST_DEFAULT_LIMIT = 25;
export const ASSISTANT_LIST_MAX_LIMIT = 500;
/**
 * A narrowing id filter, which a query string may repeat or carry once.
 *
 * The array must hold something. An empty one is refused rather than treated as the filter being
 * absent, which is what a caller that drops empty parameters from a query string would otherwise
 * turn it into: a request to see only these assistants, answered with every assistant.
 */
function NonEmptyIdFilterSchema(description) {
    return z
        .union([z.uuid(), z.array(z.uuid()).min(1)])
        .optional()
        .describe(description);
}
/**
 * The filters and page the assistant list accepts.
 *
 * Flat scalars throughout. The route also still accepts the `page: { size, after }` object its
 * browser callers send, which a middleware rewrites into `limit` and `cursor` before validation;
 * publishing both would offer a reader two ways to say the same thing, one of which a query
 * string cannot carry unambiguously.
 */
export const AssistantListQuerySchema = z
    .object({
    ids: NonEmptyIdFilterSchema('Return only these assistants.'),
    organizationId: z
        .uuid()
        .optional()
        .describe('Return only assistants owned by this organization.'),
    practiceIds: NonEmptyIdFilterSchema('Return only assistants answering for these practices.'),
    name: z
        .string()
        .optional()
        .describe('Return only assistants whose name contains this text, case-insensitively.'),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(ASSISTANT_LIST_MAX_LIMIT)
        .default(ASSISTANT_LIST_DEFAULT_LIMIT)
        .describe(`Assistants per page. Defaults to ${ASSISTANT_LIST_DEFAULT_LIMIT.toString()}, up to ${ASSISTANT_LIST_MAX_LIMIT.toString()}.`),
    cursor: z
        .string()
        .min(1)
        .optional()
        .describe('Opaque cursor returned as meta.page.after by the previous page.'),
    sort: z
        .string()
        .regex(SORT_QUERY_PARAMETER_REGEX)
        .optional()
        .describe('Order by created or name, comma-separated, each optionally prefixed with - for descending. Any other column is refused.'),
})
    .strict();
export const ASSISTANT_CHAT_SESSION_LIST_DEFAULT_LIMIT = 50;
export const ASSISTANT_CHAT_SESSION_LIST_MAX_LIMIT = 1_000;
/** The columns the conversation list can be ordered by. */
export const AssistantChatSessionSortColumnSchema = z.enum([
    'created',
    'updated',
    'voiceCallStartTime',
    'voiceCallEndTime',
    'isTest',
]);
/**
 * An id filter, which a query string may repeat or carry once, parsed to the collection the
 * handler filters with either way.
 */
function IdFilterSchema(description) {
    return OneOrManySchema(z.uuid())
        .transform((value) => [value].flat())
        .optional()
        .describe(description);
}
/**
 * One end of the created range. A calendar date names a whole UTC day, and both ends are
 * inclusive, so `before` closes at the last instant of the day rather than the midnight that
 * opens it. An exact instant is used as given.
 */
function CreatedBoundSchema(options) {
    return z
        .union([z.iso.date(), z.iso.datetime()])
        .transform((value) => options.closesTheDay && !value.includes('T')
        ? new Date(`${value}T23:59:59.999Z`)
        : new Date(value))
        .optional()
        .describe(options.description);
}
/** How the transcript of one conversation is ordered. */
export const AssistantVoiceCallQuerySchema = z
    .object({
    orderBy: z
        .enum(['created', 'updated'])
        .default('created')
        .describe('The message field the transcript is ordered by.'),
    orderByDirection: z
        .enum(SortOrder)
        .default(SortOrder.Ascending)
        .describe('asc reads the call from its first utterance, desc from its last.'),
})
    .strict();
/**
 * The filters and page the conversation list accepts.
 *
 * Every parameter is a flat scalar or a repeated scalar, because that is all a query string
 * carries unambiguously: an id filter accepts one value or many rather than requiring bracket
 * syntax for a single one.
 */
export const AssistantChatSessionListQuerySchema = z
    .object({
    ids: IdFilterSchema('Return only these chat sessions.'),
    organizationIds: IdFilterSchema('Return only conversations owned by these organizations.'),
    encounterIds: IdFilterSchema('Return only conversations about these appointments.'),
    modality: z
        .enum(AssistantChatSessionModality)
        .optional()
        .describe('Return only spoken (VOICE) or only written (TEXT) conversations.'),
    after: CreatedBoundSchema({
        closesTheDay: false,
        description: 'Return only conversations created at or after this instant, or from the start of this UTC date.',
    }),
    before: CreatedBoundSchema({
        closesTheDay: true,
        description: 'Return only conversations created at or before this instant, or through the end of this UTC date.',
    }),
    phoneNumber: z
        .string()
        .optional()
        .describe('Return only calls placed to or from a number containing these digits. At least three digits are required for the filter to apply.'),
    type: z
        .enum(AssistantVoiceCallType)
        .optional()
        .describe('Return only calls that reached the assistant this way.'),
    endedReason: z
        .string()
        .optional()
        .describe('Return only calls the telephony vendor ended for this exact reason.'),
    voiceAssistantIds: IdFilterSchema('Return only calls answered by these voice assistants, across their versions.'),
    practiceIds: IdFilterSchema('Return only calls whose answering assistant belonged to these practices.'),
    isTest: QueryBooleanSchema.optional().describe('Return only test conversations, or only real ones. true, false, 1 or 0.'),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(ASSISTANT_CHAT_SESSION_LIST_MAX_LIMIT)
        .default(ASSISTANT_CHAT_SESSION_LIST_DEFAULT_LIMIT)
        .describe(`Conversations per page. Defaults to ${ASSISTANT_CHAT_SESSION_LIST_DEFAULT_LIMIT.toString()}, up to ${ASSISTANT_CHAT_SESSION_LIST_MAX_LIMIT.toString()}.`),
    offset: z.coerce
        .number()
        .int()
        .nonnegative()
        .default(0)
        .describe('How many conversations to skip; add limit to it to read the next page.'),
    orderBy: AssistantChatSessionSortColumnSchema.default('created'),
    orderByDirection: z.enum(SortOrder).default(SortOrder.Descending),
})
    .strict();
//# sourceMappingURL=query.js.map